import invariant from "invariant";
import { ReduceStore } from "flux/utils";
import Dispatcher from "Dispatcher";
import PropTypes from "prop-types";
import SuperRecordActions from "data/SuperRecordActions";
import typeCheckingStore from "data/util/typeCheckingStore";
import SuperRecordListActions from "data/SuperRecordListActions";
import RecordActions, {
    OPT_USER_CONFIRMED,
    OPT_USER_INITIATED,
} from "data/RecordActions";
import TableAccess from "data/util/TableAccess";
import entityMetaStore from "data/meta/entityMetaStore";
import TableProxy from "data/util/TableProxy";
import EntityProxy from "data/util/EntityProxy";
import FileUploadActions from "./FileUploadActions";
import MappingActions from "./MappingActions";
import { isConfirmed } from "./OverStore";
import EntityName from "data/util/EntityName";
import StereotypesStore from "data/StereotypesStore";
import SuperRecordStore from "data/SuperRecordStore";
import { getImplementations } from "data/selectors/stereotypes";
import { isVersionInfluencing } from "data/selectors/entityFields";
import NavigatorStore from "data/NavigatorStore";
import { ACTION_CONTEXT_KEY } from "data/util/ContextedReduceStore";
import MapButtonActions from "data/MapButtonActions";
import MapDialogActions from "data/MapDialogActions";
import SearchResultActions from "data/SearchResultActions";
import MappingStore from "data/MappingStore";
import TableMappingVisitor from "data/TableMappingVisitor";
import MergeActions from "./MergeActions";
import MergeStore, { MERGE_PLUGINS } from "./MergeStore";
import ClientId from "data/ClientId";

const isEntityAction = action =>
    EntityName.is(action.recordKey);

export const isUnconfirmedVersionChange = (tableStoreOrAccess, action) => {
    if (! action[OPT_USER_INITIATED] || action[OPT_USER_CONFIRMED]) {
        // only confirm user actions need confirmation
        return false;
    }
    if (!isVersionInfluencing(action.recordKey, action.fieldKey)) {
        // only confirm version-influencing fields
        return false;
    }
    let access;
    if (tableStoreOrAccess instanceof TableStore) {
        access = new TableAccess(tableStoreOrAccess.getState());
    } else if (tableStoreOrAccess instanceof TableAccess) {
        access = tableStoreOrAccess;
    } else {
        throw new Error("Illegal tableStoreOrAccess passed");
    }
    // will the new value change the version?
    const ei = entityMetaStore.getInfo(action.recordKey);
    invariant(ei.type === "ROOT", "Only ROOT entities can change versions, not %s", ei.type);
    const tp = new TableProxy(access, ei.tableKey, action.recordId);
    const ep = new EntityProxy(tp, action.recordKey);
    if (process.env.NODE_ENV === "development") {
        // sanity checks
        if (!tp.isEntity(ei) || !ep.isCorrectVersion()) {
            console.warn(`Record ${action.recordId} isn't a '${action.recordKey}'?!`);
        }
    }
    access.updateEntityRecordField(action.recordKey, action.recordId, action.fieldKey, action.value);
    return !ep.isCorrectVersion();
};

const takeSnapshot = state => ({
        ...state,
        _snapshot: clearSnapshot(state),
    });

// eslint-disable-next-line no-unused-vars
const getSnapshot = state =>
    state._snapshot;

const clearSnapshot = state => {
    if (state._snapshot == null) return state;
    state = {...state};
    delete state._snapshot;
    return state;
};

const restoreSnapshot = state => {
    const snap = state._snapshot;
    if (snap == null) return state; // nothing to do
    state = clearSnapshot(state);
    for (const tk of Object.keys(snap.tables)) {
        for (const id of Object.keys(snap.tables[tk])) {
            state.tables[tk][id] = snap.tables[tk][id];
        }
    }
    return state;
};

const _mapping_workflow = state => {

    // ensure MappingStore goes first
    Dispatcher.waitFor([ // todo: get this.dispatcher...
        MappingStore.getDispatchToken(),
    ]);

    // If MappingStore didn't change, I won't either.
    if (!MappingStore.hasChanged()) return state;

    // If MappingStore isn't ready, wait until it is.
    if (!MappingStore.isReady()) return state;

    /*
     * We are GO!
     */

    const tableAccess = new TableAccess(state);
    const snap = getSnapshot(state);
    new TableMappingVisitor(
        MappingStore.getState(),
        tableAccess,
        snap == null ? null : new TableAccess(snap, true),
    ).execute();

    return tableAccess.getState();
};

const _do_final_global_merge_work = (state) => {
    if(MergeStore.isReady()) return state; // not merging anything
    const access = new TableAccess(state);
    // update any global references of source id to target id
    const mergeState = MergeStore.getState();
    access.remapGlobalMergeTargetToSource(mergeState.target, mergeState.source);
    return clearSnapshot(access.getState()); // finally cleanup the snapshot.
};

const _do_final_mapping_work = (state) => {
    if(MergeStore.isReady()) return clearSnapshot(state); // not merging anything, mapping completed.
    const access = new TableAccess(state);
    switch(MergeStore.getPluginKey()) {
        case MERGE_PLUGINS.RECORD_MERGE: {
            const action = MergeStore.getDeleteAction();
            const args = [
                action.recordKey,
                action.recordId,
            ];
            isEntityAction(action)
                ? access.deleteEntityRecord(...args)
                : access.deleteTableRecord(...args);
            return clearSnapshot(access.getState());
        }

        case MERGE_PLUGINS.CHILD_MERGE: {
            const action = MergeStore.getDeleteAction();
            const args = [
                action.recordKey,
                action.recordId,
                action.fieldKey,
                action.childId
            ];
            isEntityAction(action)
                ? access.deleteEntityChild(...args)
                : access.deleteTableChild(...args);
            return clearSnapshot(access.getState());
        }
        // no client delete necessary.
        case MERGE_PLUGINS.GLOBAL_MERGE:
            // global merge is not complete until SuperRecordActions.UPDATED
            // happens. Keep the snapshot until then.
            return state;
        default:
            // unknown merge plugin.
            return clearSnapshot(state);
    }
};

/**
 * I store table-level record data. I replace PersistenceContext (drink!). I
 * don't manage loading state; a record is either here or not, period.
 */
export class TableStore extends ReduceStore {

    getInitialState() {
        return {
            tables: {},
            listQueue: {},
        };
    }

    reduce(state, action) {
        switch (action.type) {

            case SuperRecordActions.LOADED:
            case SuperRecordActions.CREATED: {
                const access = new TableAccess(state);
                access.loadSuperRecordPacket(action.data);
                return _mapping_workflow(access.getState());
            }

            case SuperRecordActions.UPDATED: {
                return _do_final_global_merge_work(
                    new TableAccess(state)
                        .loadSuperRecordPacket(action.data)
                        .getState()
                );
            }

            case SuperRecordListActions.LOADED: {
                const access = new TableAccess(state);
                action.data.forEach(packet =>
                    access.loadSuperRecordPacket(packet));
                access.processListQueue(action.key, action.data.map(r => r.id));
                return _mapping_workflow(access.getState());
            }

            case FileUploadActions.COMPLETE: {
                const access = new TableAccess(state);
                let value = {...action.file, ...action.value};
                if (value._multiValued) {
                    value = access.addToEntityCollection(action.recordKey, action.recordId, action.fieldKey, value);
                }
                const args = [
                    action.recordKey,
                    action.recordId,
                    action.fieldKey,
                    value,
                ];
                if (isEntityAction(action)) {
                    access.updateEntityRecordField(...args);
                } else {
                    access.updateTableRecordColumn(...args);
                }
                return access.getState();
            }

            case RecordActions.SET_FIELD: {
                const access = new TableAccess(state);
                const args = [
                    action.recordKey,
                    action.recordId,
                    action.fieldKey,
                    action.value,
                ];
                if (isEntityAction(action)) {
                    if (isUnconfirmedVersionChange(this, action)) {
                        return state;
                    }
                    access.updateEntityRecordField(...args);
                } else {
                    access.updateTableRecordColumn(...args);
                }
                return access.getState();
            }

            case SuperRecordActions.NEW:
            case RecordActions.NEW: {
                const access = new TableAccess(state);
                const args = [
                    action.recordKey,
                    action.newId,
                ];
                isEntityAction(action)
                    ? access.createEntityRecord(...args)
                    : access.createTableRecord(...args);
                return access.getState();
            }

            case RecordActions.NEW_CHILD: {
                const access = new TableAccess(state);
                const args = [
                    action.recordKey,
                    action.recordId,
                    action.fieldKey,
                    action.newId,
                    action.insertAt,
                ];
                isEntityAction(action)
                    ? access.createEntityChild(...args)
                    : access.createTableChild(...args);
                return access.getState();
            }

            case RecordActions.DELETE_CHILD: {
                if (! isConfirmed(action)) return state;
                const access = new TableAccess(state);
                const args = [
                    action.recordKey,
                    action.recordId,
                    action.fieldKey,
                    action.childId
                ];
                isEntityAction(action)
                    ? access.deleteEntityChild(...args)
                    : access.deleteTableChild(...args);
                return access.getState();
            }

            case SuperRecordActions.NEW_MAGIC_CHILD: {
                // this one is different. it receives _stereotypical_ coords and
                // an explicit entityKey to create, along with the desired newId
                return new TableAccess(state)
                    .createMagicChild(
                        action.recordId,
                        action.entityKey,
                        action.newId,
                    )
                    .getState();
            }

            case MergeActions.DO_MERGE: {
                if(!isConfirmed(action)) return state;
                return takeSnapshot(state);
            }

            case MappingActions.__PROCESS_NEXT_IN_QUEUE: {
                return takeSnapshot(state);
            }


            case MapButtonActions.LAUNCH_DIALOG:
            case MergeActions.LAUNCH_DIALOG: {
                return takeSnapshot(state);
            }

            case MapButtonActions.AUTOMAP_TO_NEW_MAGIC_CHILD: {
                const magic = NavigatorStore.getMagicState(action[ACTION_CONTEXT_KEY]);
                const access = new TableAccess(state);
                access.createMagicChild(
                    magic.id,
                    action.entityKey,
                    action.newId,
                );
                // Because of circular deps, don't do workflow here; mapping
                // will do it again.
                return clearSnapshot(access.getState());
            }

            case MapButtonActions.AUTOMAP_FROM: {
                return clearSnapshot(_mapping_workflow(state));
            }

            case MappingActions.MODEL_LOADED:
            case MapDialogActions.TOGGLE_EXCLUSION:
            case MapDialogActions.SET_MAGIC_ROOT_EXCLUSION:
            case MapDialogActions.IMPORT:
            case SearchResultActions.SEARCH_RESULT:
            case MappingActions.__DO_IT_AGAIN: {
                return _mapping_workflow(state);
            }

            // not MergeActions.CANCEL_FOR_SOURCE_OPTLOCK!
            case MergeActions.CANCEL:
            case MapDialogActions.CANCEL:
            case SuperRecordActions.CANCEL_FOR_OPTLOCK: {
                return restoreSnapshot(state);
            }

            case MappingActions.IT_IS_DONE: {
                return _do_final_mapping_work(state);
            }

            case RecordActions.DELETE_RECORD: {
                if (! isConfirmed(action)) return state;
                const access = new TableAccess(state);
                const args = [
                    action.recordKey,
                    action.recordId
                ];
                isEntityAction(action)
                    ? access.deleteEntityRecord(...args)
                    : access.deleteTableRecord(...args);
                return access.getState();
            }

            case SuperRecordActions.DELETED: {
                const access = new TableAccess(state);
                const stereoInfo = StereotypesStore.getStereotypeByKey(action.key);
                const implementations = getImplementations(stereoInfo, access, action.id);
                if (implementations.length === 0) {
                    return access.getState();
                }
                const entityInfo = entityMetaStore.getInfo(implementations[0].key);
                const srInfo = SuperRecordStore.getRecordLO(action.key, action.id).getValue();
                // clean up the magic children
                if (srInfo && srInfo.topLevelIds) {
                    for (const mck of Object.keys(srInfo.topLevelIds)) {
                        for (const mcid of srInfo.topLevelIds[mck]) {
                            access.deleteEntityRecord(mck, mcid);
                        }
                    }
                }
                // do the magic root
                access.deleteEntityRecord(entityInfo.key, action.id);
                return access.getState();
            }

            default:
                return state;
        }
    }

    hasRecord(table, id) {
        return new TableAccess(this.getState()).hasRecord(table, id);
    }

    hasEntityRecord(entityKey, id) {
        const ei = entityMetaStore.getInfo(entityKey);
        return new TableAccess(this.getState()).hasRecord(ei.tableKey, id);
    }

    getRecordData(table, id) {
        return new TableAccess(this.getState()).getRecordData(table, id);
    }

    getColumnData(table, id, column) {
        return new TableAccess(this.getState()).getColumnData(table, id, column);
    }

    getEntityProxy(entityKey, id) {
        return new TableAccess(this.getState()).getEntityProxy(entityKey, id);
    }

    getStereotypeProxy(stereoKey, id) {
        return new TableAccess(this.getState()).getStereotypeProxy(stereoKey, id);
    }

    getSnapshot() {
        return getSnapshot(this.getState());
    }

    isWaiting() {
        const q = this.getState().listQueue;
        // This is `Object.isEmpty(q)` or something
        for (const ek in q) if (q.hasOwnProperty(ek)) return true;
        return false;
    }

}

TableStore.stateTypes = {
    tables: PropTypes.objectOf( // table key
        PropTypes.objectOf( // id
            PropTypes.shape({
                meta: PropTypes.oneOfType([
                    PropTypes.shape({
                        id: PropTypes.number.isRequired,
                        ts: PropTypes.number.isRequired,
                        waiting: PropTypes.bool.isRequired,
                    }),
                    PropTypes.shape({
                        id: ClientId.propType.isRequired,
                        waiting: PropTypes.bool.isRequired,
                    }),
                ]).isRequired,
                // arbitrary stuff
                data: PropTypes.object.isRequired,
            }),
        ),
    ).isRequired,
    listQueue: PropTypes.objectOf(
        // these are passed a TableAccess and an array of IDs (the list)
        PropTypes.arrayOf(PropTypes.func)
    ).isRequired,
    _snapshot: PropTypes.any,
};

export default typeCheckingStore(new TableStore(Dispatcher));



// WEBPACK FOOTER //
// ./app/data/TableStore.js